import React, {useEffect, useState, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {Card, CardBody, Row, Col, Button, Alert} from "reactstrap";
import ReactGA from "react-ga4";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {usePayment} from "../contexts/PaymentContext";
import {useUser} from "../contexts/AuthContext";
import {GoogleAnalyticsEventCategories} from "../constants";
import PageHeader from "../components/Common/PageHeader";

const PurchaseSuccess: React.FC = () => {
  const navigate = useNavigate();
  const {user} = useUser();
  const {getTokenBalance, tokenBalance} = usePayment();
  const [isPolling, setIsPolling] = useState(true);
  const initialBalanceRef = useRef<number | null>(null);
  const pollCountRef = useRef(0);

  useEffect(() => {
    // Track purchase success page view
    ReactGA.event({
      category: GoogleAnalyticsEventCategories.PAYMENT,
      action: "purchase success page viewed",
      label: `tokens: ${tokenBalance?.tokens || "unknown"}`
    });

    // Store initial balance
    if (tokenBalance && initialBalanceRef.current === null) {
      initialBalanceRef.current = tokenBalance.tokens ?? 0;
    }

    // Poll for token balance updates (Stripe webhook may take a few seconds)
    if (user.authenticated && isPolling) {
      const pollInterval = setInterval(() => {
        pollCountRef.current += 1;
        getTokenBalance(true); // Force refresh

        // Stop polling after 20 attempts (40 seconds) or if balance increased
        if (
          pollCountRef.current >= 20 ||
          (tokenBalance && initialBalanceRef.current !== null && tokenBalance.tokens > initialBalanceRef.current)
        ) {
          setIsPolling(false);
          clearInterval(pollInterval);
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    }

    // Redirect to files page after balance is updated or polling completes
    if (!isPolling) {
      const timer = setTimeout(() => {
        // Track automatic redirect
        ReactGA.event({
          category: GoogleAnalyticsEventCategories.PAYMENT,
          action: "auto redirect from success page",
          label: "to files page"
        });
        navigate("/files");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [user.authenticated, getTokenBalance, navigate, tokenBalance, isPolling]);

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <PageHeader
            title="Payment Successful!"
            subtitle="Your token purchase has been completed"
            icon="fa fa-check-circle text-success"
          />
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <div className="text-center">
                    <i className="fa fa-check-circle fa-5x text-success mb-4" />
                    <h4>Thank you for your purchase!</h4>
                    <p className="lead">Your tokens have been added to your account.</p>
                    {isPolling ? (
                      <Alert color="info">
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Updating your balance...
                      </Alert>
                    ) : (
                      tokenBalance && (
                        <Alert color="success">
                          <strong>Current Balance: {tokenBalance.tokens} tokens</strong>
                        </Alert>
                      )
                    )}
                    <p className="text-muted">
                      {isPolling
                        ? "Please wait while we confirm your purchase..."
                        : "You will be redirected to your files in a few seconds..."}
                    </p>
                    <div className="mt-4">
                      <Button
                        color="primary"
                        data-color={color}
                        size="lg"
                        onClick={() => {
                          // Track manual navigation to files
                          ReactGA.event({
                            category: GoogleAnalyticsEventCategories.PAYMENT,
                            action: "manual navigate from success page",
                            label: "to files page"
                          });
                          navigate("/files");
                        }}
                      >
                        <i className="fa fa-file mr-2" />
                        Go to Your Files
                      </Button>
                      <Button
                        color="secondary"
                        data-color={color}
                        size="lg"
                        className="ml-2"
                        onClick={() => {
                          // Track manual navigation to upload
                          ReactGA.event({
                            category: GoogleAnalyticsEventCategories.PAYMENT,
                            action: "manual navigate from success page",
                            label: "to upload page"
                          });
                          navigate("/upload");
                        }}
                      >
                        <i className="fa fa-upload mr-2" />
                        Upload a Log
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default PurchaseSuccess;
